import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { toast } from 'sonner';
import { Phone, ShieldCheck, Store, Truck, UserCog, Loader2, AlertCircle, MessageSquare, MessageCircle, ArrowLeft } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { getSubdomainSlug } from '../../utils/tenantResolver';
import { isNative } from '../../utils/nativeBridge';
import { loginReturnPath } from '../../utils/loginIntent';
import axios from 'axios';
import { loginErrorMessage } from '../../utils/loginErrorMessage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESEND_DELAY = 30;

const THEME_VARS = {
  'theme-modern-organic': { primary: '#2E3B32', bg: '#FDFBF7', surface: '#FFFFFF', text: '#1A1D1A', muted: '#5C635A' },
  'theme-country': { primary: '#00B37A', bg: '#FFFFFF', surface: '#FFFFFF', text: '#1A1A1A', muted: '#6B7280' },
  'theme-zepto': { primary: '#5B2EBF', bg: '#F5F0FF', surface: '#FFFFFF', text: '#1A1A2E', muted: '#6B7280' },
  'theme-instamart': { primary: '#0C831F', bg: '#F4F6F9', surface: '#FFFFFF', text: '#1A1D1A', muted: '#6B7280' },
  'theme-blinkit': { primary: '#F7C948', bg: '#FFFFFF', surface: '#FFFFFF', text: '#1A1A1A', muted: '#6B7280' },
};

const TenantLoginPage = ({ riderMode: riderModeProp }) => {
  const { slug: pathSlug } = useParams();
  const subdomainSlug = getSubdomainSlug();
  const slug = subdomainSlug || pathSlug;
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, registerWithStore, user } = useAuth();

  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [notRiderAlert, setNotRiderAlert] = useState(false);
  const [otpMethod, setOtpMethod] = useState('sms');
  const [resendTimer, setResendTimer] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(true);

  const isRiderApp = riderModeProp
    || window.location.pathname === '/rider'
    || new URLSearchParams(window.location.search).get('app') === 'rider';
  const [selectedRole, setSelectedRole] = useState(isRiderApp ? 'delivery_boy' : 'customer');

  useEffect(() => {
    const loadTenant = async () => {
      try {
        const res = await axios.get(`${API}/store/by-slug/${slug}`);
        setTenant(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoadingTenant(false);
      }
    };
    loadTenant();
  }, [slug]);

  useEffect(() => {
    if (user && tenant) {
      if (user.admin_id === tenant.admin_id) {
        navigate((user.role === 'customer' && loginReturnPath()) || '/');
      } else if (user.role === 'admin' && user.id === tenant.admin_id) {
        navigate('/admin');
      }
    }
  }, [user, tenant, navigate]);

  // Resolve display name and logo based on app type (rider vs customer)
  const displayName = isRiderApp
    ? (tenant?.rider_app_name || tenant?.store_name || 'Store')
    : (tenant?.customer_app_name || tenant?.store_name || 'Store');
  const displayLogo = isRiderApp
    ? (tenant?.rider_app_logo || tenant?.store_logo)
    : (tenant?.customer_app_logo || tenant?.store_logo);

  // Update page title
  useEffect(() => {
    if (displayName) {
      document.title = displayName;
    }
  }, [displayName]);

  // Countdown timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const theme = THEME_VARS[tenant?.theme] || THEME_VARS['theme-modern-organic'];

  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const doSendOtp = useCallback(async (method) => {
    setLoading(true);
    try {
      await sendOtp(phone, method);
      toast.success(`OTP sent via ${method === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
      setStep('otp');
      setResendTimer(RESEND_DELAY);
    } catch { toast.error('Failed to send OTP'); }
    finally { setLoading(false); }
  }, [phone, sendOtp]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) { toast.error('Enter a valid phone number'); return; }
    if (!agreedToTerms) { toast.error('Please agree to Terms & Conditions and Privacy Policy'); return; }
    await doSendOtp(otpMethod);
  };

  const handleResend = async (method) => {
    setOtpMethod(method);
    await doSendOtp(method);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { toast.error('Enter complete OTP'); return; }
    setLoading(true);
    try {
      const loggedUser = await verifyOtp(phone, otp, selectedRole);
      toast.success('Login successful!');

      if (isRiderApp && loggedUser.role === 'customer') {
        setNotRiderAlert(true);
        return;
      }

      if (loggedUser.role === 'customer') {
        if (!loggedUser.admin_id || loggedUser.admin_id !== tenant.admin_id) {
          await registerWithStore(tenant.admin_id);
        }
        // Back to where login was asked for (e.g. Add Address), if anywhere.
        navigate(loginReturnPath() || '/');
      } else if (loggedUser.role === 'admin' || loggedUser.role === 'branch_admin') {
        navigate('/admin');
      } else if (loggedUser.role === 'delivery_boy') {
        navigate('/delivery');
      } else if (loggedUser.role === 'super_admin') {
        navigate('/super-admin');
      }
    } catch (error) { toast.error(loginErrorMessage(error)); }
    finally { setLoading(false); }
  };

  if (loadingTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#FAFAFA' }}>
        <Store className="w-16 h-16 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Store Not Found</h1>
        <p className="text-gray-500 mb-6">No store found for &quot;{slug}&quot;</p>
        <Button onClick={() => navigate('/login')} variant="outline">Go to Main Login</Button>
      </div>
    );
  }

  if (notRiderAlert) {
    const alertLogoUrl = resolveUrl(tenant?.store_logo);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: theme.bg }} data-testid="not-rider-alert">
        <div className="w-full max-w-md text-center">
          {alertLogoUrl && !logoError ? (
            <img src={alertLogoUrl} alt={tenant?.store_name} className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 border border-black/10" onError={() => setLogoError(true)} />
          ) : (
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: theme.primary }}>
              <Truck className="w-8 h-8 text-white" />
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-lg border border-black/5 p-6 space-y-4">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Not Registered as a Rider</h2>
            <p className="text-gray-500 text-sm">
              Your account ({phone}) is not registered as a delivery partner for {tenant?.store_name || 'this store'}.
              Please contact the store administrator to be added as a rider.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={() => navigate('/')} className="w-full" style={{ background: theme.primary, color: '#fff' }} data-testid="continue-as-customer-btn">
                Continue as Customer
              </Button>
              <Button onClick={() => { setNotRiderAlert(false); setStep('phone'); setOtp(''); }} variant="outline" className="w-full" data-testid="try-different-number-btn">
                Try a Different Number
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const allRoles = [
    { id: 'customer', label: 'Customer', icon: Store },
    { id: 'admin', label: 'Store Admin', icon: UserCog },
    { id: 'delivery_boy', label: 'Delivery', icon: Truck },
  ];
  const roles = isRiderApp
    ? allRoles.filter(r => r.id === 'delivery_boy' || r.id === 'admin')
    : allRoles;

  const logoUrl = resolveUrl(displayLogo);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: theme.bg }}>
      {/* Back Button */}
      <div className="p-3">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors"
          style={{ color: theme.text }}
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Tenant Brand */}
        <div className="text-center mb-8" data-testid="tenant-brand">
          {logoUrl && !logoError ? (
            <img src={logoUrl} alt={displayName} className="w-20 h-20 rounded-2xl object-cover mx-auto mb-4 border border-black/10" onError={() => setLogoError(true)} />
          ) : (
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: theme.primary }}>
              <Store className="w-10 h-10 text-white" />
            </div>
          )}
          <h1 className="text-3xl font-bold" style={{ color: theme.text }}>{displayName}</h1>
          <p style={{ color: theme.muted }} className="mt-1 text-sm">
            {tenant.store_tagline || 'Order fresh groceries & milk'}
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl p-6 shadow-lg border border-black/5" style={{ background: theme.surface }}>
          {step === 'phone' ? (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: theme.text }}>Login / Sign Up</h2>
                <p className="text-sm mt-1" style={{ color: theme.muted }}>Enter your phone number</p>
              </div>

              {/* Role Selection — hidden on native apps */}
              {!isNative() && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">I am a</Label>
                <div className="grid grid-cols-3 gap-2">
                  {roles.map(r => (
                    <button
                      key={r.id} type="button"
                      onClick={() => setSelectedRole(r.id)}
                      className={`p-2.5 rounded-xl border-2 transition-all text-center ${
                        selectedRole === r.id ? 'border-current shadow-sm' : 'border-gray-200'
                      }`}
                      style={selectedRole === r.id ? { borderColor: theme.primary, color: theme.primary } : {}}
                      data-testid={`role-${r.id}`}
                    >
                      <r.icon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs font-medium">{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              <div>
                <Label>Phone Number</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-medium text-gray-500 px-2">+91</span>
                  <Input
                    type="tel" value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter phone number"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* OTP Method Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Send OTP via</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="otp-method-sms"
                    onClick={() => setOtpMethod('sms')}
                    className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 transition-all ${
                      otpMethod === 'sms' ? 'border-current shadow-sm' : 'border-gray-200'
                    }`}
                    style={otpMethod === 'sms' ? { borderColor: theme.primary, color: theme.primary } : {}}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm font-medium">SMS</span>
                  </button>
                  <button
                    type="button"
                    data-testid="otp-method-whatsapp"
                    onClick={() => setOtpMethod('whatsapp')}
                    className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 transition-all ${
                      otpMethod === 'whatsapp' ? 'border-current shadow-sm' : 'border-gray-200'
                    }`}
                    style={otpMethod === 'whatsapp' ? { borderColor: theme.primary, color: theme.primary } : {}}
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">WhatsApp</span>
                  </button>
                </div>
              </div>

              {/* T&C Checkbox */}
              <div className="flex items-start gap-2" data-testid="terms-checkbox-wrapper">
                <Checkbox
                  id="tenant-terms-agree"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  data-testid="terms-checkbox"
                  className="mt-0.5"
                />
                <span className="text-xs leading-relaxed" style={{ color: theme.muted }}>
                  I agree to the{' '}
                  <a
                    href={`/legal/${slug}/terms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                    style={{ color: theme.primary }}
                    data-testid="terms-link"
                  >Terms & Conditions</a>{' '}and{' '}
                  <a
                    href={`/legal/${slug}/privacy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                    style={{ color: theme.primary }}
                    data-testid="privacy-link"
                  >Privacy Policy</a>
                </span>
              </div>

              <Button
                type="submit" disabled={loading || phone.length < 10 || !agreedToTerms}
                className="w-full text-white py-6 rounded-xl text-base font-semibold"
                style={{ background: theme.primary }}
                data-testid="send-otp-btn"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Sending...</> : 'Send OTP'}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="text-center mb-4">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2" style={{ color: theme.primary }} />
                <h2 className="text-xl font-bold" style={{ color: theme.text }}>Verify OTP</h2>
                <p className="text-sm mt-1" style={{ color: theme.muted }}>
                  Enter the 6-digit code sent to +91 {phone}
                </p>
              </div>

              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={handleVerifyOtp} disabled={loading || otp.length !== 6}
                className="w-full text-white py-6 rounded-xl text-base font-semibold"
                style={{ background: theme.primary }}
                data-testid="verify-otp-btn"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying...</> : 'Verify & Login'}
              </Button>

              {/* Resend OTP section */}
              <div className="text-center space-y-2" data-testid="resend-section">
                {resendTimer > 0 ? (
                  <p className="text-sm" style={{ color: theme.muted }} data-testid="resend-timer">
                    Resend OTP in <span className="font-semibold" style={{ color: theme.primary }}>{resendTimer}s</span>
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm" style={{ color: theme.muted }}>Didn&apos;t receive the code?</p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleResend('sms')}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-sm font-medium hover:underline disabled:opacity-50"
                        style={{ color: theme.primary }}
                        data-testid="resend-sms-btn"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Resend SMS
                      </button>
                      <span style={{ color: theme.muted }}>|</span>
                      <button
                        type="button"
                        onClick={() => handleResend('whatsapp')}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-sm font-medium text-green-600 hover:underline disabled:opacity-50"
                        data-testid="resend-whatsapp-btn"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        Resend WhatsApp
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button onClick={() => { setStep('phone'); setOtp(''); setResendTimer(0); }} className="w-full text-center text-sm" style={{ color: theme.muted }}>
                Change phone number
              </button>
            </div>
          )}
        </div>

        {/* Browse without login (customer app only) */}
        {!isRiderApp && (
        <button
          onClick={() => navigate('/')}
          className="w-full text-center mt-4 py-3 text-sm font-medium hover:underline"
          style={{ color: theme.primary }}
          data-testid="browse-without-login"
        >
          Browse products without login
        </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default TenantLoginPage;
